import { useState, useEffect, useContext } from 'react';
import {
  DevHubTextField,
  DevHubFormControl,
  DevHubAutocomplete,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import { slugify } from '../../util/stringFormat';
import { UserContext } from '../../contexts/UserContext';

/**
 * The schema for the {@link DBaaSAppPicker} properties.
 */
export const DBaaSAppPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'object',
    properties: {
      applicationId: {
        type: 'number',
        title: 'Application Id',
        description: 'The ID of the application',
      },
      name: {
        type: 'string',
        title: 'Application Name',
        description: 'The name of the application',
      },
      appslug: {
        type: 'string',
        title: 'App Slug',
        description: 'The slug of the application',
      },
    },
    required: ['applicationId', 'name', 'appslug'],
  },
};

type App = {
  name: string;
  applicationId: number;
  appslug: string;
  status: string;
};

const GetUserRoleBasedApplicationsQuery = gql`
  query GetUserRoleBasedApplications(
    $login: String!
    $filter: ResourceCatalogUserApplicationFilter
  ) {
    identity {
      user(login: $login) {
        login
        resources {
          applicationsPaged(filter: $filter) {
            applications {
              name
              applicationId
              status
            }
          }
        }
      }
    }
  }
`;

/**
 * The input props that can be specified under `ui:options` for the
 * `DBaaSAppPicker` field extension.
 *
 * @public
 */
export type DBaaSAppState = FieldExtensionComponentProps<App | undefined>;

/**
 * The underlying component that is rendered in the form for the `DBaaSAppPicker`
 * field extension. Filters to only applications where the current user holds
 * `dbaas_prod` or `dbaas_nonprod` roles. The `havingRoles` filter uses SQL IN
 * semantics (OR), so a single query suffices.
 * @param props - The properties passed to the component.
 * @public
 */
export const DBaaSAppPicker = (props: DBaaSAppState) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;
  const [apps, setApps] = useState<App[]>([]);
  const { login } = useContext(UserContext);

  const [fetchApps, { data, loading, error }] = useLazyQuery(
    GetUserRoleBasedApplicationsQuery,
    {
      errorPolicy: 'all',
      variables: {
        login: login,
        filter: { havingRoles: ['dbaas_prod', 'dbaas_nonprod'] },
      },
    },
  );

  useEffect(() => {
    if (login) {
      fetchApps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const allApps: App[] =
      data?.identity?.user?.resources?.applicationsPaged?.applications ?? [];

    const filtered = allApps
      .filter((app: App) => app.status !== 'Retired')
      .map((app: App) =>
        Object.assign({}, app, { appslug: slugify(app.name) }),
      );

    filtered.sort((a: App, b: App) => a?.name?.localeCompare(b?.name));
    setApps(filtered);
  }, [data]);

  const onSelect = (_: any, value: App | null) => {
    onChange(value ?? undefined);
  };

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="No Applications Available"
          helperText="Failed to fetch application details."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={formData}
          loading={loading}
          loadingText="Loading.."
          options={apps}
          getOptionLabel={option => option?.name}
          onChange={onSelect}
          autoSelect
          renderInput={params => (
            <DevHubTextField
              {...params}
              label={
                apps?.length > 0
                  ? 'Applications available'
                  : 'No assigned applications, please reach out to your team.'
              }
              margin="normal"
              helperText="Applications with DBaaS Prod or NonProd access"
              variant="outlined"
              required={required}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </DevHubFormControl>
  );
};
